import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
// hooks
import useAuth from 'src/hooks/useAuth';
// utils
import { useFriendlyName } from 'src/utils/automacoes.utils';
// components
import Iconify from 'src/components/Iconify';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import ModalImportarJSON from 'src/components/modal/ModalImportarJSON';
import { downloadJSON, buildEnvelope } from 'src/utils/exportImport.utils';
import { toPortableAutomacao, getAutomacaoResetWarnings } from 'src/pages/dashboard/Automacoes/automacaoPortable';
// ui
import { Switch } from 'src/@/components/ui/switch';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/@/components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'src/@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'src/@/components/ui/dropdown-menu';
// table
import GenericTable from 'src/components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from 'src/components/table/GenericTable.design';
// sections
import DialogCriarAutomacao from 'src/sections/@dashboard/automacoes/DialogCriarAutomacao';
import { DialogLogsAutomacao } from 'src/sections/@dashboard/automacoes/DialogLogsAutomacao';
// actions
import v2_actionEditarAutomacao from 'src/actions/v2/automacoes/v2_actionEditarAutomacao';
import v2_actionDeletarAutomacao from 'src/actions/v2/automacoes/v2_actionDeletarAutomacao';
import v2_actionAutomacoes from 'src/actions/v2/automacoes/v2_actionAutomacoes';
import v2_actionAutomacao from 'src/actions/v2/automacoes/v2_actionAutomacao';
import v2_actionCriarAutomacao from 'src/actions/v2/automacoes/v2_actionCriarAutomacao';
import {
    v2_actionListarGruposAutomacoes,
    v2_actionCriarGrupoAutomacoes,
    v2_actionEditarGrupoAutomacoes,
    v2_actionDeletarGrupoAutomacoes,
} from 'src/actions/v2/automacoes/v2_actionGruposAutomacoes';
import { fDateTime } from 'src/utils/formatTime';
import { AUTOMACOES_DESIGN as D } from './Automacoes.design';

// ----------------------------------------------------------------------

const CardAutomacao = ({ automacao, onSelect, onDelete, onChangeStatus, grupos, onMoveToGroup, onExport }) => {
    const getFriendlyName = useFriendlyName();
    const { t } = useTranslation(['automation', 'common']);
    return (
    <div onClick={onSelect} className={D.autoCard}>
        <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
                <h4 className={D.autoCardTitle}>{automacao?.name}</h4>
                <span className={D.autoCardDate}>{fDateTime(automacao?.create_at)}</span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
                {automacao?.draft ? (
                    <span className={`${D.badge} ${D.badgeNeutral}`}>
                        {t('status.draft', { ns: 'common', defaultValue: 'Rascunho' })}
                    </span>
                ) : (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Switch
                            checked={automacao?.status}
                            onCheckedChange={(checked) => onChangeStatus(automacao._id, checked)}
                            className={D.switch}
                        />
                    </div>
                )}
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        onExport();
                    }}
                    className={D.iconButton}
                    title={t('list.card.exportTitle', 'Exportar automação')}
                >
                    <Iconify icon="solar:export-bold" width={16} />
                </button>
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        onDelete();
                    }}
                    className={D.iconButtonDanger}
                    title={t('list.card.deleteTitle', 'Excluir automação')}
                >
                    <Iconify icon="material-symbols:delete-outline" width={16} />
                </button>
            </div>
        </div>

        {!automacao?.draft && (
            <div className={D.flowRow}>
                <div className={D.flowChip}>
                    {getFriendlyName(automacao?.params?.from?.action)}
                </div>
                <Iconify icon="mdi:arrow-right" className={D.flowArrow} width={16} />
                <div className={D.flowChipAlt}>
                    {automacao?.params?.to?.length > 0 && getFriendlyName(automacao?.params?.to[0].value)}
                </div>
                {automacao?.params?.to?.length > 1 && (
                    <span className={`${D.badge} ${D.badgeNeutral}`}>
                        +{automacao.params.to.length - 1}
                    </span>
                )}
            </div>
        )}

        <div className={D.autoCardFooter}>
            <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button type="button" className={D.folderButton}>
                            <Iconify icon="mdi:folder-outline" width={14} />
                            {automacao.group_id ? grupos.find((g) => g._id === automacao.group_id)?.name || t('list.noGroup', 'Sem grupo') : t('list.noGroup', 'Sem grupo')}
                            <Iconify icon="mdi:chevron-down" className="opacity-50" width={12} />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[180px]">
                        <DropdownMenuItem onClick={() => onMoveToGroup(automacao._id, null)} className="text-[#71717a]">
                            <Iconify icon="mdi:folder-open-outline" className="mr-2" width={16} />
                            {t('list.noGroup', 'Sem grupo')}
                        </DropdownMenuItem>
                        {grupos.map((grupo) => (
                            <DropdownMenuItem key={grupo._id} onClick={() => onMoveToGroup(automacao._id, grupo._id)}>
                                <Iconify icon="mdi:folder-outline" className="mr-2" width={16} />
                                {grupo.name}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    </div>
    );
};

const GrupoAutomacoes = ({
    grupo,
    automacoes,
    onSelectAutomacao,
    onDeleteAutomacao,
    onChangeStatus,
    onEditGrupo,
    onDeleteGrupo,
    grupos,
    onMoveToGroup,
    onExportAutomacao,
}) => {
    const { t } = useTranslation('automation');
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <div className={D.groupCard}>
            <div className={D.groupHeader} onClick={() => setIsExpanded(!isExpanded)}>
                <div className="flex items-center gap-3 min-w-0">
                    <span className={`${D.groupCaret} ${isExpanded ? D.groupCaretOpen : ''}`}>▶</span>
                    <div className={D.groupIcon}>
                        <Iconify icon="mdi:folder" width={18} />
                    </div>
                    <h3 className={D.groupTitle}>{grupo.name}</h3>
                    <span className={`${D.badge} ${D.badgeNeutral}`}>{automacoes.length}</span>
                </div>
                <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button type="button" className={D.iconButton}>
                                <Iconify icon="mdi:dots-vertical" width={16} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEditGrupo(grupo)}>
                                <Iconify icon="mdi:pencil-outline" className="mr-2" width={16} />
                                {t('list.group.edit', 'Editar grupo')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => onDeleteGrupo(grupo)}
                                className="text-[#b42318] focus:text-[#b42318] focus:bg-[#fef3f2]"
                            >
                                <Iconify icon="mdi:delete-outline" className="mr-2" width={16} />
                                {t('list.group.delete', 'Excluir grupo')}
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
            {isExpanded && (
                <div className={D.groupBody}>
                    {automacoes.length > 0 ? (
                        <div className={D.autoGrid}>
                            {automacoes.map((automacao, index) => (
                                <CardAutomacao
                                    key={automacao._id || index}
                                    automacao={automacao}
                                    onSelect={() => onSelectAutomacao(automacao)}
                                    onDelete={() => onDeleteAutomacao(automacao)}
                                    onChangeStatus={onChangeStatus}
                                    grupos={grupos}
                                    onMoveToGroup={onMoveToGroup}
                                    onExport={() => onExportAutomacao(automacao)}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className={D.groupEmpty}>{t('list.group.empty', 'Nenhuma automação neste grupo')}</p>
                    )}
                </div>
            )}
        </div>
    );
};

const DialogCriarGrupo = ({ open, onClose, onConfirm, grupoEditando = null }) => {
    const { t } = useTranslation(['automation', 'common']);
    const [nomeGrupo, setNomeGrupo] = useState(grupoEditando?.name || '');

    useEffect(() => {
        if (grupoEditando) {
            setNomeGrupo(grupoEditando.name);
        } else {
            setNomeGrupo('');
        }
    }, [grupoEditando]);

    const handleSubmit = () => {
        if (nomeGrupo.trim()) {
            onConfirm(nomeGrupo.trim(), grupoEditando);
            setNomeGrupo('');
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="bg-white border border-[#e7e7ec] rounded-[12px]">
                <DialogHeader>
                    <DialogTitle className={D.dialogTitle}>
                        {grupoEditando ? t('list.group.dialog.editTitle', 'Editar Grupo') : t('list.group.dialog.createTitle', 'Criar Novo Grupo')}
                    </DialogTitle>
                    <DialogDescription className={D.dialogDesc}>
                        {grupoEditando
                            ? t('list.group.dialog.editDescription', 'Edite o nome do grupo de automações')
                            : t('list.group.dialog.createDescription', 'Crie um novo grupo para organizar suas automações')}
                    </DialogDescription>
                </DialogHeader>
                <div className={D.field}>
                    <Label htmlFor="nome-grupo" className={D.label}>
                        {t('list.group.dialog.nameLabel', 'Nome do Grupo')}
                    </Label>
                    <Input
                        id="nome-grupo"
                        value={nomeGrupo}
                        onChange={(e) => setNomeGrupo(e.target.value)}
                        placeholder={t('list.group.dialog.namePlaceholder', 'Ex: Automações de Vendas')}
                        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                        className={D.input}
                    />
                    <p className={D.hint}>
                        {t('list.group.dialog.nameHint', 'Use grupos para organizar automações por time, funil ou objetivo.')}
                    </p>
                </div>
                <DialogFooter className={D.dialogFooter}>
                    <button type="button" onClick={onClose} className={D.ghostButton}>
                        {t('buttons.cancel', { ns: 'common', defaultValue: 'Cancelar' })}
                    </button>
                    <button type="button" onClick={handleSubmit} disabled={!nomeGrupo.trim()} className={D.primaryButton}>
                        {grupoEditando
                            ? t('buttons.save', { ns: 'common', defaultValue: 'Salvar' })
                            : t('buttons.create', { ns: 'common', defaultValue: 'Criar' })}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default function Automacoes() {
    const { cliente, user } = useAuth();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation(['automation', 'common', 'notifications']);
    const getFriendlyName = useFriendlyName();
    const [automacoes, setAutomacoes] = useState([]);
    const [filteredAutomacoes, setFilteredAutomacoes] = useState([]);
    const [statusFilter, setStatusFilter] = useState('todos');
    const [sortOrder, setSortOrder] = useState('recent');
    const [searchQuery, setSearchQuery] = useState('');
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
    const [openModalImportar, setOpenModalImportar] = useState(false);
    const [idAutomacao, setIdAutomacao] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(false);

    const [grupos, setGrupos] = useState([]);
    const [openDialogGrupo, setOpenDialogGrupo] = useState(false);
    const [grupoEditando, setGrupoEditando] = useState(null);
    const [viewMode, setViewMode] = useState('grupos');
    const [loadingGrupos, setLoadingGrupos] = useState(false);

    const carregarGrupos = async () => {
        try {
            setLoadingGrupos(true);
            const response = await v2_actionListarGruposAutomacoes(cliente._id);
            if (!response.data.error) {
                setGrupos(response.data.data);
            }
        } catch (error) {
            console.error('Erro ao carregar grupos:', error);
            enqueueSnackbar(t('list.toasts.loadGroupsError', 'Erro ao carregar grupos de automações'), { variant: 'error' });
        } finally {
            setLoadingGrupos(false);
        }
    };

    async function getAutomacoes() {
        try {
            const response = await v2_actionAutomacoes(cliente._id);
            const automacoesDaAPI = response.data.data;
            setAutomacoes(automacoesDaAPI);
            setFilteredAutomacoes(automacoesDaAPI);
        } catch (err) {
            console.log(err);
        }
    }

    async function deleteAutomacao(id) {
        try {
            setLoadingDelete(true);
            await v2_actionDeletarAutomacao(id);
            await getAutomacoes();
            setLoadingDelete(false);
        } catch (err) {
            setLoadingDelete(false);
            console.log(err);
        }
    }

    const handleExportAutomacao = async (automacao) => {
        try {
            let full = automacao;
            if (!full?.params) {
                const res = await v2_actionAutomacao(automacao._id);
                full = res.data.data;
            }
            const portable = toPortableAutomacao(full, t);
            const safeName = (full.name || 'automacao').replace(/[^\w\-]+/g, '_');
            downloadJSON(`${safeName}.automacao.json`, buildEnvelope('lais-automacao', portable));
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('list.toasts.exportError', 'Erro ao exportar automação'), { variant: 'error' });
        }
    };

    const handleImportAutomacao = async (data) => {
        const createRes = await v2_actionCriarAutomacao(cliente._id, {
            scope: { name: data.name, userId: user.id },
        });
        const newId = createRes.data.data._id;
        await v2_actionEditarAutomacao(newId, {
            scope: { name: data.name, status: false, params: data.params },
        });
        await getAutomacoes();
        return { warnings: getAutomacaoResetWarnings(data, t) };
    };

    const handleEditAutomation = async (id, status) => {
        let data = { scope: { status: status } };
        try {
            await v2_actionEditarAutomacao(id, data);
            enqueueSnackbar(t('shared.toasts.updateSuccess', 'Automação editada com sucesso!'), { variant: 'success' });
            getAutomacoes();
        } catch (error) {
            console.error('Erro ao editar automação:', error);
            enqueueSnackbar(t('shared.toasts.saveError', 'Erro ao salvar automação'), { variant: 'error' });
            return;
        }
    };

    const criarGrupo = async (nome, grupoEditando = null) => {
        try {
            if (grupoEditando) {
                const response = await v2_actionEditarGrupoAutomacoes(grupoEditando._id, { name: nome });
                if (!response.data.error) {
                    enqueueSnackbar(t('list.toasts.groupUpdateSuccess', 'Grupo editado com sucesso!'), { variant: 'success' });
                    await carregarGrupos();
                }
            } else {
                const response = await v2_actionCriarGrupoAutomacoes(cliente._id, { name: nome });
                if (!response.data.error) {
                    enqueueSnackbar(t('list.toasts.groupCreateSuccess', 'Grupo criado com sucesso!'), { variant: 'success' });
                    await carregarGrupos();
                }
            }
        } catch (error) {
            console.error('Erro ao gerenciar grupo:', error);
            enqueueSnackbar(t('list.toasts.groupManageError', 'Erro ao gerenciar grupo'), { variant: 'error' });
        }
    };

    const deletarGrupo = async (grupo) => {
        try {
            const response = await v2_actionDeletarGrupoAutomacoes(grupo._id);
            if (!response.data.error) {
                enqueueSnackbar(t('list.toasts.groupDeleteSuccess', 'Grupo excluído com sucesso!'), { variant: 'success' });
                await carregarGrupos();
            }
        } catch (error) {
            console.error('Erro ao deletar grupo:', error);
            enqueueSnackbar(t('list.toasts.groupDeleteError', 'Erro ao excluir grupo'), { variant: 'error' });
        }
    };

    const editarGrupo = (grupo) => {
        setGrupoEditando(grupo);
        setOpenDialogGrupo(true);
    };

    const handleMoveToGroup = async (automacaoId, grupoId) => {
        try {
            const data = { scope: { group_id: grupoId || null } };
            await v2_actionEditarAutomacao(automacaoId, data);
            const automacoesAtualizadas = automacoes.map((automacao) =>
                automacao._id === automacaoId ? { ...automacao, group_id: grupoId || null } : automacao
            );
            setAutomacoes(automacoesAtualizadas);
            setFilteredAutomacoes(automacoesAtualizadas);
            enqueueSnackbar(t('list.toasts.moveSuccess', 'Automação movida com sucesso!'), { variant: 'success' });
        } catch (error) {
            console.error('Erro ao mover automação:', error);
            enqueueSnackbar(t('list.toasts.moveError', 'Erro ao mover automação'), { variant: 'error' });
        }
    };

    const organizarAutomacoesPorGrupos = () => {
        const automacoesComGrupo = filteredAutomacoes.filter((automacao) => automacao.group_id);
        const automacoesSemGrupo = filteredAutomacoes.filter((automacao) => !automacao.group_id);
        const gruposComAutomacoes = grupos.map((grupo) => ({
            ...grupo,
            automacoes: automacoesComGrupo.filter((automacao) => automacao.group_id === grupo._id),
        }));
        return { gruposComAutomacoes, automacoesSemGrupo };
    };

    useEffect(() => {
        getAutomacoes();
        carregarGrupos();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [statusFilter, sortOrder, searchQuery]);

    const applyFilters = () => {
        let filtered = [...automacoes];
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter((automacao) => automacao.name?.toLowerCase().includes(query));
        }
        if (statusFilter !== 'todos') {
            const isActive = statusFilter === 'active';
            filtered = filtered.filter((automacao) => automacao.status === isActive);
        }
        if (sortOrder === 'alphabetical') {
            filtered.sort((a, b) => a.name.localeCompare(b.name));
        } else if (sortOrder === 'recent') {
            filtered.sort((a, b) => new Date(b.updatedAt || b.create_at) - new Date(a.updatedAt || a.create_at));
        }
        setFilteredAutomacoes(filtered);
    };

    const { gruposComAutomacoes, automacoesSemGrupo } = organizarAutomacoesPorGrupos();

    return (
        <div className={D.root} style={{ fontFamily: D.fontStack }}>
            <ModalConfimacao
                open={openModalConfirmacao}
                onClose={() => {
                    setOpenModalConfirmacao(false);
                    setIdAutomacao(null);
                }}
                onConfirm={async () => {
                    try {
                        await deleteAutomacao(idAutomacao);
                        setOpenModalConfirmacao(false);
                        setIdAutomacao(null);
                    } catch (err) {
                        enqueueSnackbar(t('shared.toasts.deleteError', 'Erro ao excluir automação'), { variant: 'error' });
                    }
                }}
                loadingFinalizar={loadingDelete}
                mensagem={t('shared.confirmDelete', 'Deseja mesmo excluir?')}
            />

            <DialogCriarGrupo
                open={openDialogGrupo}
                onClose={() => {
                    setOpenDialogGrupo(false);
                    setGrupoEditando(null);
                }}
                onConfirm={criarGrupo}
                grupoEditando={grupoEditando}
            />

            <ModalImportarJSON
                open={openModalImportar}
                onClose={() => setOpenModalImportar(false)}
                expectedKind="lais-automacao"
                title={t('list.import.title', 'Importar Automação')}
                description={t('list.import.description', 'Selecione um arquivo .automacao.json exportado. Ela será criada inativa e os campos vinculados a outro cliente (canal, funil, usuários, etiquetas) são limpos — os passos afetados aparecerão em vermelho no editor.')}
                onImport={handleImportAutomacao}
            />

            <div className={D.stickyHeader}>
                <div className={D.container}>
                    <div className={D.topbar}>
                        <div className={D.topbarIdentity}>
                            <div className={D.iconTile}>
                                <Iconify icon="mdi:robot" width={22} />
                            </div>
                            <div className="min-w-0">
                                <h1 className={D.title}>{t('list.header.title', 'Automações')}</h1>
                                <p className={D.subtitle}>
                                    {t('list.header.subtitle', 'Crie fluxos que disparam ações sozinhas quando algo acontece no chat ou no funil.')}
                                </p>
                            </div>
                        </div>
                        <div className={D.topbarActions}>
                            <button
                                type="button"
                                className={D.ghostButton}
                                onClick={() => {
                                    setGrupoEditando(null);
                                    setOpenDialogGrupo(true);
                                }}
                                disabled={loadingGrupos}
                            >
                                {t('list.actions.newGroup', 'Novo Grupo')}
                            </button>
                            <button type="button" className={D.ghostButton} onClick={() => setOpenModalImportar(true)}>
                                {t('buttons.import', { ns: 'common', defaultValue: 'Importar' })}
                            </button>
                            <DialogLogsAutomacao user={user} />
                            <DialogCriarAutomacao
                                user={user}
                                onCallback={(data) => {
                                    setAutomacoes([...automacoes, data]);
                                    navigate(`/dashboard/automacoes/${data._id}`);
                                }}
                            />
                        </div>
                    </div>

                    <div className={`${D.toolbar} pb-[18px]`}>
                        <div className={D.searchWrap}>
                            <Iconify icon="eva:search-outline" className={D.searchIcon} width={16} />
                            <input
                                type="text"
                                placeholder={t('list.filters.searchPlaceholder', 'Buscar automação...')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={D.searchInput}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <span className={D.filterLabel}>{t('list.filters.statusLabel', 'Status')}</span>
                            <div className="relative">
                                <select
                                    className={D.filterSelect}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="todos">{t('list.filters.statusAll', 'Todos')}</option>
                                    <option value="active">{t('status.active', { ns: 'common', defaultValue: 'Ativo' })}</option>
                                    <option value="inactive">{t('status.inactive', { ns: 'common', defaultValue: 'Inativo' })}</option>
                                </select>
                                <Iconify icon="mdi:chevron-down" className={D.filterSelectIcon} width={14} />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className={D.filterLabel}>{t('list.filters.sortLabel', 'Ordenar')}</span>
                            <div className="relative">
                                <select
                                    className={D.filterSelect}
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value)}
                                >
                                    <option value="recent">{t('list.filters.sortRecent', 'Mais recente')}</option>
                                    <option value="alphabetical">{t('list.filters.sortAlphabetical', 'Ordem alfabética')}</option>
                                </select>
                                <Iconify icon="mdi:chevron-down" className={D.filterSelectIcon} width={14} />
                            </div>
                        </div>

                        <div className={D.viewMode}>
                            <button
                                type="button"
                                className={viewMode === 'grupos' ? D.viewModeBtnActive : D.viewModeBtn}
                                onClick={() => setViewMode('grupos')}
                                title={t('list.viewMode.groups', 'Visualização por Grupos')}
                            >
                                <Iconify icon="mdi:folder-table-outline" width={16} />
                            </button>
                            <button
                                type="button"
                                className={viewMode === 'lista' ? D.viewModeBtnActive : D.viewModeBtn}
                                onClick={() => setViewMode('lista')}
                                title={t('list.viewMode.list', 'Visualização em Lista')}
                            >
                                <Iconify icon="mdi:format-list-bulleted" width={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={D.scrollBody}>
                <div className={`${D.container} pt-6 ${D.pageBody}`}>
                    {filteredAutomacoes.length === 0 ? (
                        <div className={`${D.card} ${D.emptyState}`}>
                            <div className={D.emptyIcon}>
                                <Iconify icon="mdi:robot-outline" width={28} />
                            </div>
                            <h3 className={D.emptyTitle}>{t('list.empty.title', 'Nenhuma automação encontrada')}</h3>
                            <p className={D.emptyDesc}>
                                {t('list.empty.description', 'Crie uma nova automação para começar a enviar mensagens automaticamente.')}
                            </p>
                            <div className="mt-5">
                                <DialogCriarAutomacao
                                    user={user}
                                    onCallback={(data) => {
                                        setAutomacoes([...automacoes, data]);
                                        navigate(`/dashboard/automacoes/workflow/${data._id}`);
                                    }}
                                />
                            </div>
                        </div>
                    ) : viewMode === 'grupos' ? (
                        <div className={D.stack}>
                            <div className={D.sectionHeader}>
                                <div className={D.sectionHeaderMain}>
                                    <h2 className={D.sectionHeaderTitle}>{t('list.section.groups', 'Grupos')}</h2>
                                    <p className={D.sectionHeaderSub}>
                                        {t('list.section.groupsHint', 'Organize automações por pasta e ative ou desative cada fluxo.')}
                                    </p>
                                </div>
                            </div>

                            {gruposComAutomacoes.map((grupo) => (
                                <GrupoAutomacoes
                                    key={grupo._id}
                                    grupo={grupo}
                                    automacoes={grupo.automacoes}
                                    onSelectAutomacao={(automacao) => navigate(`/dashboard/automacoes/workflow/${automacao._id}`)}
                                    onDeleteAutomacao={(automacao) => {
                                        setIdAutomacao(automacao._id);
                                        setOpenModalConfirmacao(true);
                                    }}
                                    onChangeStatus={(id, status) => handleEditAutomation(id, status)}
                                    onEditGrupo={editarGrupo}
                                    onDeleteGrupo={deletarGrupo}
                                    grupos={grupos}
                                    onMoveToGroup={handleMoveToGroup}
                                    onExportAutomacao={handleExportAutomacao}
                                />
                            ))}

                            {automacoesSemGrupo.length > 0 && (
                                <div className={D.groupCard}>
                                    <div className={`${D.groupHeader} cursor-default hover:bg-transparent`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className={D.groupIcon}>
                                                <Iconify icon="mdi:folder-hidden" width={18} />
                                            </div>
                                            <h3 className={D.groupTitle}>{t('list.noGroup', 'Sem grupo')}</h3>
                                            <span className={`${D.badge} ${D.badgeNeutral}`}>{automacoesSemGrupo.length}</span>
                                        </div>
                                    </div>
                                    <div className={D.groupBody}>
                                        <div className={D.autoGrid}>
                                            {automacoesSemGrupo.map((automacao, index) => (
                                                <CardAutomacao
                                                    key={automacao._id || index}
                                                    automacao={automacao}
                                                    onSelect={() => navigate(`/dashboard/automacoes/workflow/${automacao._id}`)}
                                                    onDelete={() => {
                                                        setIdAutomacao(automacao._id);
                                                        setOpenModalConfirmacao(true);
                                                    }}
                                                    onChangeStatus={(id, status) => handleEditAutomation(id, status)}
                                                    grupos={grupos}
                                                    onMoveToGroup={handleMoveToGroup}
                                                    onExport={() => handleExportAutomacao(automacao)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={D.stack}>
                            <div className={D.sectionHeader}>
                                <div className={D.sectionHeaderMain}>
                                    <h2 className={D.sectionHeaderTitle}>{t('list.section.list', 'Lista')}</h2>
                                    <p className={D.sectionHeaderSub}>
                                        {t('list.section.listHint', 'Visão compacta de todas as automações, com status e grupo.')}
                                    </p>
                                </div>
                            </div>

                            <div className={D.cardFlush}>
                                <div className={D.tableWrap}>
                                    <GenericTable
                                        headLabels={[
                                            { id: 'nome', label: t('labels.name', { ns: 'common', defaultValue: 'Nome' }), className: 'flex-1 min-w-[220px] pr-4' },
                                            { id: 'fluxo', label: t('list.table.flow', 'Fluxo'), className: 'flex-1 min-w-[260px] pr-4' },
                                            { id: 'grupo', label: t('list.table.group', 'Grupo'), className: 'w-[160px] pr-4' },
                                            { id: 'data', label: t('labels.createdAt', { ns: 'common', defaultValue: 'Criado em' }), className: 'w-[150px] pr-4' },
                                            { id: 'status', label: t('labels.status', { ns: 'common', defaultValue: 'Status' }), className: 'w-[100px] pr-4' },
                                            { id: 'acoes', label: '', className: 'w-[80px] text-right' },
                                        ]}
                                    >
                                        {filteredAutomacoes.map((automacao) => (
                                            <div
                                                key={automacao._id}
                                                className={`${GENERIC_TABLE_DESIGN.row.container} cursor-pointer`}
                                                onClick={() => navigate(`/dashboard/automacoes/${automacao._id}`)}
                                            >
                                                <div className="flex-1 min-w-[220px] pr-4 flex items-center">
                                                    <p className={`${D.tableCell} font-semibold truncate`}>{automacao?.name}</p>
                                                </div>

                                                <div className="flex-1 min-w-[260px] pr-4 flex items-center gap-2">
                                                    {!automacao?.draft ? (
                                                        <>
                                                            <span className={D.flowChip}>
                                                                {getFriendlyName(automacao?.params?.from?.action)}
                                                            </span>
                                                            <Iconify icon="mdi:arrow-right" className={D.flowArrow} width={14} />
                                                            <span className={D.flowChipAlt}>
                                                                {automacao?.params?.to?.length > 0 && getFriendlyName(automacao?.params?.to[0].value)}
                                                            </span>
                                                            {automacao?.params?.to?.length > 1 && (
                                                                <span className={`${D.badge} ${D.badgeNeutral}`}>
                                                                    +{automacao.params.to.length - 1}
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className={D.tableCellMuted}>
                                                            {t('list.table.draftNoFlow', 'Rascunho — sem fluxo definido')}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="w-[160px] pr-4" onClick={(e) => e.stopPropagation()}>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <button type="button" className={D.folderButton}>
                                                                <Iconify icon="mdi:folder-outline" width={14} />
                                                                <span className="truncate max-w-[100px]">
                                                                    {automacao.group_id
                                                                        ? grupos.find((g) => g._id === automacao.group_id)?.name || t('list.noGroup', 'Sem grupo')
                                                                        : t('list.noGroup', 'Sem grupo')}
                                                                </span>
                                                            </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="start" className="w-[180px]">
                                                            <DropdownMenuItem onClick={() => handleMoveToGroup(automacao._id, null)} className="text-[#71717a]">
                                                                <Iconify icon="mdi:folder-open-outline" className="mr-2" width={16} />
                                                                {t('list.noGroup', 'Sem grupo')}
                                                            </DropdownMenuItem>
                                                            {grupos.map((grupo) => (
                                                                <DropdownMenuItem key={grupo._id} onClick={() => handleMoveToGroup(automacao._id, grupo._id)}>
                                                                    <Iconify icon="mdi:folder-outline" className="mr-2" width={16} />
                                                                    {grupo.name}
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>

                                                <div className="w-[150px] pr-4">
                                                    <span className={`${D.tableCellMuted} capitalize`}>{fDateTime(automacao?.create_at)}</span>
                                                </div>

                                                <div className="w-[100px] pr-4 flex items-center" onClick={(e) => e.stopPropagation()}>
                                                    {automacao?.draft ? (
                                                        <span className={`${D.badge} ${D.badgeNeutral}`}>
                                                            {t('status.draft', { ns: 'common', defaultValue: 'Rascunho' })}
                                                        </span>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <Switch
                                                                checked={automacao?.status}
                                                                onCheckedChange={(checked) => handleEditAutomation(automacao._id, checked)}
                                                                className={D.switch}
                                                            />
                                                            <span className={automacao?.status ? 'text-[12px] font-semibold text-[#067647]' : 'text-[12px] font-semibold text-[#a1a1aa]'}>
                                                                {automacao?.status
                                                                    ? t('status.active', { ns: 'common', defaultValue: 'Ativo' })
                                                                    : t('status.inactive', { ns: 'common', defaultValue: 'Inativo' })}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="w-[80px] flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleExportAutomacao(automacao)}
                                                                    className={D.iconButton}
                                                                >
                                                                    <Iconify icon="solar:export-bold" width={16} />
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="left">
                                                                <p>{t('list.card.exportTitle', 'Exportar automação')}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setIdAutomacao(automacao._id);
                                                                        setOpenModalConfirmacao(true);
                                                                    }}
                                                                    className={D.iconButtonDanger}
                                                                >
                                                                    <Iconify icon="material-symbols:delete-outline" width={16} />
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="left">
                                                                <p>{t('list.card.deleteTitle', 'Excluir automação')}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            </div>
                                        ))}
                                    </GenericTable>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
